import type { PollService } from "@busbud/core-types";

import { shouldIncludeFees } from "@app/helpers/price";
import { isBoBTrip } from "@app/helpers/udm";
import { getDisplayPrice } from "@app/helpers/udm/get-display-price";
import * as types from "@app/modules/results/constants/action-types";
import { TripType } from "@app/modules/results/constants/departure-types";
import type {
  AddToAsyncCartPriceHasChangedAction,
  AddToCartBeginAction,
  AddToCartFailureAction,
  AddToCartPriceHasChangedAcceptedAction,
  AddToCartPriceHasChangedAction,
  AddToCartPriceHasChangedRefusedAction,
  AddToCartSuccessAction
} from "@app/modules/results/types/results-action-types";
import type NapiClient from "@app/services/api";
import type { NapiV2Cart } from "@app/types/napi-v2-cart-types";
import type { ResultsReduxState } from "@app/types/results-redux-types";
import type { SearchInfo } from "@app/types/search-types";
import type { PassengerSeatSelectionInstance } from "@app/types/seat-selection-types";
import { redirect, refresh } from "@app/utils/window";

import type { AnyAction, ThunkAction } from "@reduxjs/toolkit";
import type { Dispatch } from "@reduxjs/toolkit";

import { createCart } from "./cart/";
import { getDisplayedPriceConfig } from "../../selectors/get-displayed-price-config";
import { isAsyncCart } from "../../selectors/get-page";

type AddToCartActionType = ThunkAction<
  void,
  ResultsReduxState,
  undefined,
  AnyAction
>;

// TODO-SSF: Add new function with dynamic first argument. string or trip object. If
export const addToCart =
  (
    trip_or_trip_id:
      | PollService.API.RelatedPublicTrip
      | PollService.API.RelatedPublicTripV4
      | string,
    passengers: PassengerSeatSelectionInstance[] = [],
    search_override: SearchInfo | null = null,
    api?: NapiClient
  ): AddToCartActionType =>
  (dispatch, getState) => {
    const { search, config, whitelabel } = getState();
    const is_async_cart = isAsyncCart(getState());

    let trip:
      | PollService.API.RelatedPublicTrip
      | PollService.API.RelatedPublicTripV4;
    if (typeof trip_or_trip_id === "string") {
      const { trips } = getState();
      // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      trip = trips[trip_or_trip_id];
    } else {
      trip = trip_or_trip_id;
    }
    const trip_id = trip.id;
    const trip_type = isBoBTrip(trip) ? TripType.BOB : TripType.BOX;
    const sources = trip.prices.map(({ source_name }) => source_name);

    const displayed_price_config = {
      ...getDisplayedPriceConfig(getState()),
      sources
    };

    const include_fees = shouldIncludeFees(displayed_price_config);

    const price = getDisplayPrice(trip, "trip", displayed_price_config);

    const _search = search_override || search;
    const cart = createCart(_search, whitelabel);

    dispatch(addToCartBegin(trip_id, trip_type, !is_async_cart));

    return cart
      .reserve(trip_id, config.user_country, passengers)
      .then(() => {
        const inner_cart = cart.getCart() as NapiV2Cart | null;

        if (
          cart.isSamePriceAsBefore(price, {
            include_fees
          })
        ) {
          dispatch(
            addToCartSuccess(
              trip_id,
              inner_cart,
              passengers,
              api,
              is_async_cart
            )
          );
        } else {
          dispatch(
            addToCartPriceHasChanged(trip_id, inner_cart, is_async_cart)
          );
        }
      })
      .catch((e: Error) => {
        dispatch(addToCartFailure(trip_id, e));
      });
  };

export function addToCartAcceptNewPrice(
  departure_or_trip_id: string,
  is_async_cart: boolean
) {
  return (dispatch: Dispatch) => {
    return dispatch(
      addToCartPriceHasChangedAccepted(
        departure_or_trip_id,
        null,
        is_async_cart
      )
    );
  };
}

type addToCartRefuseNewPriceActionType = (
  dispatch: (action: any) => void,
  getState: () => ResultsReduxState
) => void;

export const addToCartRefuseNewPrice =
  (
    id: string,
    is_async_cart: boolean,
    url_redirect?: string
  ): addToCartRefuseNewPriceActionType =>
  dispatch => {
    dispatch(addToCartPriceHasChangedRefused(id, is_async_cart));
    url_redirect ? redirect(url_redirect) : refresh();
  };

export function addToCartDiscard() {
  return {
    type: types.ADD_TO_CART_DISCARD
  };
}

const addToCartBegin = (
  id: string,
  departure_type: TripType,
  with_modal: boolean = true
): AddToCartBeginAction => ({
  type: types.ADD_TO_CART_BEGIN,
  id,
  departure_type,
  with_modal
});

const addToCartPriceHasChanged = (
  id: string,
  cart: NapiV2Cart | null,
  is_async_cart: boolean
): AddToCartPriceHasChangedAction => ({
  type: types.ADD_TO_CART_PRICE_HAS_CHANGED,
  id,
  cart,
  is_async_cart
});

export const addToAsyncCartPriceHasChanged = (
  id: string,
  cart: NapiV2Cart | null
): AddToAsyncCartPriceHasChangedAction => ({
  type: types.ADD_TO_ASYNC_CART_PRICE_HAS_CHANGED,
  id,
  cart
});

const addToCartPriceHasChangedAccepted = (
  id: string,
  cart: NapiV2Cart | null,
  is_async_cart: boolean
): AddToCartPriceHasChangedAcceptedAction => ({
  type: types.ADD_TO_CART_PRICE_HAS_CHANGED_ACCEPTED,
  id,
  cart,
  is_async_cart
});

const addToCartPriceHasChangedRefused = (
  id: string,
  is_async_cart: boolean
): AddToCartPriceHasChangedRefusedAction => ({
  type: types.ADD_TO_CART_PRICE_HAS_CHANGED_REFUSED,
  id,
  is_async_cart
});

const addToCartFailure = (id: string, error: Error): AddToCartFailureAction => {
  return {
    type: types.ADD_TO_CART_FAILURE,
    id,
    error
  };
};

const addToCartSuccess = (
  id: string,
  cart: NapiV2Cart | null,
  selected_seats?: PassengerSeatSelectionInstance[],
  api?: NapiClient,
  is_async_cart: boolean = false
): AddToCartSuccessAction => ({
  type: types.ADD_TO_CART_SUCCESS,
  id,
  cart,
  selected_seats,
  api,
  is_async_cart
});
